/**
 * <collection-filter-list>
 *
 * Turns the tiles rendered by sections/collection-filter-slider.liquid into
 * proxies for the collection page's facet checkboxes.
 *
 * Each tile carries data-filter-name (e.g. "Hoodies"). Clicking it finds the
 * matching checkbox inside a <form is="facet-form"> and clicks it natively, so
 * FacetForm's own 'change' handler (assets/facets.js) performs the filtering.
 * Nothing here talks to facet internals or builds URLs itself.
 *
 * Tiles are additive: they toggle their own filter on and off and stack with
 * each other and with the regular filter UI.
 */
if (!customElements.get('collection-filter-list')) {
  class CollectionFilterList extends HTMLElement {
    constructor() {
      super();
      this.onTileClick = this.onTileClick.bind(this);
      this.syncState = this.syncState.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onTileClick);

      // facets.js fires this after every AJAX re-render, when the checkboxes
      // have been replaced and tile state needs recomputing.
      document.addEventListener('collection:rerendered', this.syncState);

      this.syncState();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onTileClick);
      document.removeEventListener('collection:rerendered', this.syncState);
    }

    get tiles() {
      return Array.from(this.querySelectorAll('[data-filter-name]'));
    }

    normalize(value) {
      return (value || '').trim().toLowerCase();
    }

    /**
     * Filters render twice: context 'vertical' (desktop, in
     * main-collection-product-grid) and context 'drawer' (mobile). Each lives in
     * its own form and FacetForm.buildUrl() reads only its own FormData, so the
     * click has to land in whichever form belongs to the current viewport.
     * Input ids are prefixed with the context, which makes that cheap to detect.
     */
    isMobile() {
      if (window.FoxTheme && FoxTheme.config && typeof FoxTheme.config.mqlMobile === 'boolean') {
        return FoxTheme.config.mqlMobile;
      }
      return window.matchMedia('(max-width: 767px)').matches;
    }

    facetForms() {
      const forms = Array.from(document.querySelectorAll('form[is="facet-form"]'));
      const preferred = this.isMobile() ? 'drawer-' : 'vertical-';

      // Forms whose inputs match the active context come first; the other
      // context stays as a fallback so a tile still works if only one renders.
      return forms.sort((a, b) => {
        const aMatch = a.querySelector(`input[type="checkbox"][id^="${preferred}"]`) ? 0 : 1;
        const bMatch = b.querySelector(`input[type="checkbox"][id^="${preferred}"]`) ? 0 : 1;
        return aMatch - bMatch;
      });
    }

    /**
     * Facet ids are positional (`<context>-<param_name>-<index>`) so they can't
     * be used for lookup. Match on the input's value, falling back to the
     * visible label text, which is what the merchant sees when naming the tile.
     */
    findCheckbox(filterName) {
      const target = this.normalize(filterName);
      if (!target) return null;

      for (const form of this.facetForms()) {
        const inputs = Array.from(form.querySelectorAll('input[type="checkbox"]'));

        const byValue = inputs.find((input) => this.normalize(input.value) === target);
        if (byValue) return byValue;

        const byLabel = inputs.find((input) => {
          if (!input.id) return false;
          const label = form.querySelector(`label[for="${CSS.escape(input.id)}"]`);
          if (!label) return false;
          const text = label.querySelector('.reversed-link__text') || label;
          return this.normalize(text.textContent) === target;
        });
        if (byLabel) return byLabel;
      }

      return null;
    }

    onTileClick(event) {
      const tile = event.target.closest('[data-filter-name]');
      if (!tile || !this.contains(tile)) return;

      event.preventDefault();

      const checkbox = this.findCheckbox(tile.dataset.filterName);

      // No such filter on this collection, or Shopify disabled it because the
      // value has no matching products. Leave the page untouched.
      if (!checkbox || checkbox.disabled) {
        this.markUnavailable(tile, true);
        return;
      }

      // A native click toggles checked and emits a bubbling 'change', which is
      // exactly what FacetForm listens for.
      checkbox.click();
    }

    markUnavailable(tile, unavailable) {
      const card = tile.closest('.collection-filter-card');
      if (card) card.classList.toggle('collection-filter-card--unavailable', unavailable);
      if (unavailable) {
        tile.setAttribute('aria-disabled', 'true');
      } else {
        tile.removeAttribute('aria-disabled');
      }
    }

    syncState() {
      this.tiles.forEach((tile) => {
        const checkbox = this.findCheckbox(tile.dataset.filterName);
        const unavailable = !checkbox || checkbox.disabled;

        this.markUnavailable(tile, unavailable);
        tile.setAttribute('aria-pressed', checkbox && checkbox.checked ? 'true' : 'false');
      });
    }
  }

  customElements.define('collection-filter-list', CollectionFilterList);
}
